/**
 * App store
 */
import { defineStore } from 'pinia';
import { isMobile } from '@star/utils';
import { StorageKeysEnum, TemplateEnum, ThemeEnum } from '@star/constants';
import { TEMPLATE_MAP } from '@star/template';
import { useLocalStorage, useUrlSearchParams } from '@vueuse/core';
import dayjs from 'dayjs';
import { setFullbackLocale, setI18nLanguage } from '@star/languages';
import { ROUTER_MODE } from '@star/config';
import { storeToRefs, useTenantStore } from '../../index';
import { random } from 'lodash-es';

const APP_THEME = import.meta.env.VITE_THEME_NAME; // 项目配置的主题

type Config = {
  theme: ThemeEnum;
  template: TemplateEnum;
  headerHeight: number;
  language: string;
};

const defaultConfig = {
  theme: ThemeEnum.LIGHT,
  template: TemplateEnum.BIGWIN,
  headerHeight: 0,
  language: 'en',
};

export const useAppStore = defineStore({
  id: 'app',
  state: () => {
    return {
      serviceWorkerInstalled: false,
      theme: useLocalStorage<ThemeEnum>(StorageKeysEnum.APP_THEME, ThemeEnum.LIGHT),
      template: useLocalStorage(StorageKeysEnum.APP_TEMPLATE, APP_THEME || TemplateEnum.BIGWIN),
      language: useLocalStorage(StorageKeysEnum.APP_LANGUAGE, ''),
      headerHeight: 0,
      isPullRefresh: false, // 页面下拉刷新
      isPageLoading: false, // 页面加载loading
      formRoute: {
        name: '',
        fullPath: '',
      } as { name: any; fullPath: string },
    };
  },
  getters: {},
  actions: {
    async init() {
      this.setTemplate();
      this.setTheme();
      await this.initLanguage();
    },
    async initLanguage() {
      const { language } = useUrlSearchParams(ROUTER_MODE);
      const browserLocaleFull = navigator.language;
      let browserLocale: any = 'cn';
      if (browserLocaleFull.toLocaleLowerCase() === 'zh-cn') {
        browserLocale = 'cn';
      } else {
        browserLocale = browserLocaleFull.split('-')[0];
      }
      const appLanguage = import.meta.env.VITE_LANG_NAME;
      const lang = language || this.language || appLanguage || browserLocale;
      console.log('-----lang', language, this.language, appLanguage, browserLocale, navigator.language);
      await this.changeLanguage(lang);
      setFullbackLocale(lang);
    },
    setTheme(themeName?: string) {
      // const { theme } = useUrlSearchParams(ROUTER_MODE);
      // const _theme = (themeName ?? theme) as ThemeEnum;
      const _theme = document.documentElement.getAttribute('data-theme-v') as ThemeEnum;
      this.theme = _theme || ThemeEnum.LIGHT;
      // document.documentElement.setAttribute('data-theme-v', this.theme)
    },
    setTemplate(templateName?: string) {
      const { template } = useUrlSearchParams(ROUTER_MODE);
      const _template = (templateName || template) as string;
      if (_template) {
        const _template_drives = `${_template}${isMobile() ? 'APP' : 'PC'}`;
        if (TEMPLATE_MAP[_template]) {
          this.template = _template;
        } else if (TEMPLATE_MAP[_template_drives]) {
          this.template = _template_drives;
        }
      } else {
        // 其他逻辑
        this.template = TemplateEnum.BIGWIN;
      }
    },
    setHeaderHeight(val: number) {
      this.headerHeight = val;
    },
    async changeLanguage(language: string) {
      this.language = language;
      dayjs.locale(language);
      await setI18nLanguage(language);
    },
    setPullRefresh(val: boolean) {
      this.isPullRefresh = val;
    },
    setPageLoading(val: boolean) {
      this.isPageLoading = val;
    },
    // 获取轮询时长
    getPollingDuration() {
      const { tenantInfo } = storeToRefs(useTenantStore());
      const start = tenantInfo.value?.sysOptions?.ROTATION_TRAINING_RANDOM_NUMBERS_1;
      const end = tenantInfo.value?.sysOptions?.ROTATION_TRAINING_RANDOM_NUMBERS_2;
      const val = (random((start || 2) * 10, (end || 5) * 10) / 10) * 60 * 1000;
    },
    updateServerWork(status = true) {
      this.serviceWorkerInstalled = status;
    },
  },
});
